import React from 'react';
import BackLink from './BackLink';
import StatusPill from './StatusPill';
import clsx from 'clsx';

/**
 * DetailPageHeader — Tier-1 canonical component for entity detail pages.
 * Renders a consistent header with back navigation, title, status, and actions.
 *
 * @param {string} title - The entity title (e.g., customer name, job number)
 * @param {string} [subtitle] - Optional subtitle text
 * @param {string} [status] - Status string passed to StatusPill
 * @param {string} [statusLabel] - Optional label override for the status pill (IDI-556)
 * @param {string} [statusTone] - Optional tone override for the status pill (IDI-556)
 * @param {ReactNode|ReactNode[]} [pills] - Optional inline pills rendered next to title
 *   (e.g., "🔒 Locked at acceptance"). Wraps below title on narrow viewports.
 *   Additive prop introduced by PT-43; call sites that omit it see no layout change.
 * @param {ReactNode} [actions] - Right-aligned action buttons
 * @param {string} [backHref] - URL for back navigation link
 * @param {object} [backState] - Router state passed with the back link (IDI-157)
 * @param {string} [backLabel] - Label for back link (default: "Back")
 * @param {string} [className] - Additional CSS classes
 */
export function DetailPageHeader({ title, subtitle, status, statusLabel, statusTone, pills, actions, backHref, backTo, backState, backLabel = 'Back', className }) {
  // §5 (2026-07-29): accept `backTo` as an alias for `backHref`. `PageHeader` uses
  // `backTo`; `DetailPageHeader` originally required `backHref`. The mismatch caused
  // ReviewAndSendShell (which passes `backTo`) to render no back link. Accept both.
  const back = backHref || backTo;
  const pillList = pills == null ? [] : (Array.isArray(pills) ? pills : [pills]);
  return (
    <header data-portica-role="page-header" className={clsx('mb-6', className)}>
      {back && (
        <div className="mb-3">
          <BackLink to={back} state={backState} label={backLabel} />
        </div>
      )}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          <h1
            className="truncate"
            style={{ font: 'var(--text-h1)', color: 'var(--color-text-default)' }}
          >
            {title}
          </h1>
          {status && <StatusPill status={status} label={statusLabel} tone={statusTone} />}
          {pillList.map((pill, idx) => (
            <React.Fragment key={idx}>{pill}</React.Fragment>
          ))}
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {actions}
          </div>
        )}
      </div>
      {subtitle && (
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>
          {subtitle}
        </p>
      )}
    </header>
  );
}

export default DetailPageHeader;
